import React, { useState } from "react";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";
import { createPromocode } from "../../services/VoucherService";
import "./AdminModalForm.css";

// Admin modal for creating promocodes with dynamic validation
const AdminCreatePromocodeModal = ({ isOpen, onClose, onCreated }) => {
  // Form state management
  const [formData, setFormData] = useState({
    code: "",
    type: "topup_amount",
    amount: "",
    currency: "",
    date_end: "",
    activations_left: "",
    user_activation_count: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Check if current type is sell discount
  const isSellDiscount = formData.type === "sell_discount";

  // Handle all form field changes with number conversion
  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (value === "" ? "" : Number(value)) : value,
    }));
    setError("");
  };

  // Validate sell discount percentage
  const validateSellDiscount = () => {
    if (isSellDiscount) {
      const pct = Number(formData.amount);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        throw new Error("For Sell discount, amount must be between 0 and 100%");
      }
    }
  };

  // Prepare API payload with proper formatting
  const getPayload = () => ({
    code: formData.code.trim().toUpperCase(),
    type: formData.type,
    amount: Number(formData.amount),
    currency: isSellDiscount ? null : formData.currency || null,
    date_end: formData.date_end ? new Date(formData.date_end).toISOString() : null,
    activations_left: Number(formData.activations_left),
    user_activation_count: Number(formData.user_activation_count),
  });

  // Main form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setSaving(true);

      // Validate sell discount before API call
      validateSellDiscount();

      // Create promocode via API
      const payload = getPayload();
      await createPromocode(payload);

      setSuccess("Promocode created successfully.");

      // Optional callback and auto-close
      if (onCreated) onCreated();
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err.message || "Failed to create promocode");
    } finally {
      setSaving(false);
    }
  };

  // Don't render if modal is closed
  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title="Create Promocode"
      actions={
        <>
          <SecondButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Creating..." : "Create Promocode"}
          </MainButton>
        </>
      }
    >
      {/* Promocode creation form */}
      <form onSubmit={handleSubmit} className="admin-modal-form">
        {/* Promocode code field */}
        <label>
          Code *
          <MainInput type="text" name="code" value={formData.code} onChange={handleChange} required disabled={saving} />
        </label>

        {/* Promocode type selector */}
        <label>
          Type *
          <div className="admin-modal-select-wrapper">
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              disabled={saving}
              className="admin-modal-select admin-modal-select-with-arrow"
            >
              <option value="topup_amount">Top-up amount</option>
              <option value="sell_discount">Sell discount</option>
            </select>
          </div>
        </label>

        {/* Amount/Discount field with dynamic label and validation */}
        <label>
          {isSellDiscount ? "Discount %" : "Amount *"}
          <MainInput
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            required
            disabled={saving}
            min={isSellDiscount ? 0 : undefined}
            max={isSellDiscount ? 100 : undefined}
            placeholder={isSellDiscount ? "0 - 100" : "Amount value"}
          />
          {isSellDiscount && (
            <span style={{ fontSize: "0.8rem", color: "var(--color-text-light)" }}>
              Value must be between 0 and 100 (percentage).
            </span>
          )}
        </label>

        {/* Currency field (hidden for sell discount) */}
        {!isSellDiscount && (
          <label>
            Currency *
            <MainInput
              type="text"
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              required={!isSellDiscount}
              disabled={saving}
              placeholder="USD / EUR etc."
            />
          </label>
        )}

        {/* Expiration date field */}
        <label>
          Expires at (UTC) *
          <MainInput
            type="datetime-local"
            name="date_end"
            value={formData.date_end}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </label>

        {/* Activations limit field */}
        <label>
          Activations left *
          <MainInput
            type="number"
            name="activations_left"
            value={formData.activations_left}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </label>

        {/* Max per user field */}
        <label>
          Max per user *
          <MainInput
            type="number"
            name="user_activation_count"
            value={formData.user_activation_count}
            onChange={handleChange}
            required
            disabled={saving}
          />
        </label>

        {/* Error message display */}
        {error && (
          <div className="admin-modal-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {/* Success message display */}
        {success && (
          <div className="admin-modal-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default AdminCreatePromocodeModal;
